import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  makeStyles,
  TextField
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Chip } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomDialog from 'src/app/components/CustomDialog';
import { STATUS_API } from 'src/app/constant/config';
import {
  assignDeviceToDriver,
  getAssignableDriverDevices
} from 'src/features/driverSlice';

const SELECT_ALL_OPTION = 'Chọn tất cả';

function ShareDeviceModal({ open, onClose, driverSelected, onSuccess }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const driverDevices = useSelector(state => state.driverSlice.driverDevices);
  const statusAssignDevice = useSelector(
    state => state.driverSlice.statusAssignDevice
  );
  const statusGetDriverDevices = useSelector(
    state => state.driverSlice.statusGetDriverDevices
  );
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedDevices([]);
      setSubmitError('');
      return;
    }

    dispatch(getAssignableDriverDevices());
  }, [dispatch, open]);

  const normalizedDevices = useMemo(() => {
    const rawDevices = Array.isArray(driverDevices)
      ? driverDevices
      : Array.isArray(driverDevices?.devices)
      ? driverDevices.devices
      : Array.isArray(driverDevices?.rows)
      ? driverDevices.rows
      : [];

    return rawDevices
      .map(device => {
        const deviceId =
          device?.device_id || device?.id || device?.deviceId || device?.imei_id;
        const licensePlate =
          device?.license_plate ||
          device?.licensePlate ||
          device?.plate ||
          device?.name;

        if (!deviceId || !licensePlate) return null;

        return {
          id: deviceId,
          label: licensePlate
        };
      })
      .filter(Boolean);
  }, [driverDevices]);

  const licensePlateOptions = useMemo(
    () => [SELECT_ALL_OPTION, ...normalizedDevices.map(device => device.label)],
    [normalizedDevices]
  );

  const allSelected =
    normalizedDevices.length > 0 &&
    selectedDevices.length === normalizedDevices.length;

  const handleSelectAll = () => {
    setSubmitError('');
    if (allSelected) {
      setSelectedDevices([]);
      return;
    }

    setSelectedDevices(normalizedDevices.map(device => device.label));
  };

  const handleLicensePlateChange = (event, value) => {
    setSubmitError('');
    const hasSelectAll = value.includes(SELECT_ALL_OPTION);

    if (hasSelectAll) {
      handleSelectAll();
      return;
    }

    setSelectedDevices(value);
  };

  const handleSubmit = async () => {
    if (!driverSelected?.user?.id) return;

    const selectedDeviceIds = normalizedDevices
      .filter(device => selectedDevices.includes(device.label))
      .map(device => device.id);

    if (selectedDeviceIds.length === 0) {
      setSubmitError('Vui lòng chọn ít nhất một biển số');
      return;
    }

    const resultAction = await dispatch(
      assignDeviceToDriver({
        userId: driverSelected.user.id,
        device_ids: selectedDeviceIds,
        driverId: null
      })
    );

    if (assignDeviceToDriver.fulfilled.match(resultAction)) {
      onClose();
      if (onSuccess) onSuccess();
    }
  };

  return (
    <CustomDialog
      title="Chia sẻ thiết bị giám sát"
      open={open}
      onClose={onClose}
      sizeSmall={true}
    >
      <Grid container spacing={3}>
        <Grid item md={12} xs={12}>
          <InputLabel className={classes.label}>
            Biển số <span className={classes.required}>*</span>
          </InputLabel>
          <Autocomplete
            multiple
            id="driver-device-multi-select"
            size="small"
            options={licensePlateOptions}
            value={selectedDevices}
            onChange={handleLicensePlateChange}
            disableCloseOnSelect
            classes={{ option: classes.option }}
            getOptionLabel={option => option}
            loading={statusGetDriverDevices === STATUS_API.PENDING}
            renderOption={(option, { selected }) => {
              const isSelectAll = option === SELECT_ALL_OPTION;

              return (
                <div
                  className={classes.optionItem}
                  onClick={isSelectAll ? handleSelectAll : undefined}
                  style={
                    isSelectAll
                      ? {
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: '#1976d2'
                        }
                      : {}
                  }
                >
                  <div className={classes.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={isSelectAll ? allSelected : selected}
                      className={classes.checkbox}
                      readOnly
                    />
                    <span className={classes.optionText}>{option}</span>
                  </div>
                </div>
              );
            }}
            renderTags={(value, getTagProps) => (
              <div className={classes.tagContainer}>
                {value
                  .filter(option => option !== SELECT_ALL_OPTION)
                  .map((option, index) => (
                    <Chip
                      key={option}
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      className={classes.chip}
                    />
                  ))}
              </div>
            )}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                error={Boolean(submitError)}
                helperText={submitError}
                className={classes.searchComponentMulti}
                placeholder={
                  selectedDevices.length === 0 ? 'Chọn biển số thiết bị' : ''
                }
                InputProps={{
                  ...params.InputProps,
                  style: {
                    height: '38px',
                    fontSize: '14px',
                    padding: '0px 8px'
                  }
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box my={3}>
        <Box className={classes.groupButtonSubmit}>
          <Button
            onClick={onClose}
            className="btn-main btn-plain mx-3"
            color="primary"
            size="large"
            variant="contained"
          >
            Thoát
          </Button>
          <div className={classes.wrapper}>
            <Button
              className="btn-main mx-3"
              color="primary"
              size="large"
              variant="contained"
              onClick={handleSubmit}
              disabled={
                statusAssignDevice === STATUS_API.PENDING ||
                statusGetDriverDevices === STATUS_API.PENDING
              }
            >
              Lưu lại
            </Button>
            {statusAssignDevice === STATUS_API.PENDING && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </Box>
      </Box>
    </CustomDialog>
  );
}

const useStyles = makeStyles(theme => ({
  label: {
    marginBottom: '12px',
    fontSize: '14px',
    color: '#858C93'
  },
  required: {
    color: 'red'
  },
  searchComponentMulti: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
      minHeight: '38px',
      maxHeight: '38px',
      paddingTop: '4px',
      paddingBottom: '4px',
      backgroundColor: '#fafafa',
      '& fieldset': {
        top: 0,
        borderRadius: '4px'
      }
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      padding: '2px 4px',
      overflow: 'hidden'
    }
  },
  option: {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center'
  },
  optionItem: {
    width: '100%',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    marginRight: '12px',
    cursor: 'pointer',
    accentColor: '#1976d2'
  },
  optionText: {
    fontSize: '14px',
    color: '#333'
  },
  tagContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    maxWidth: '100%',
    maxHeight: '32px'
  },
  chip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontWeight: 500,
    borderRadius: '16px',
    margin: '4px 6px 4px 0',
    fontSize: '13px',
    height: '28px',
    '&:hover': {
      backgroundColor: '#bbdefb'
    },
    '& .MuiChip-deleteIcon': {
      color: '#1976d2',
      fontSize: '18px',
      '&:hover': {
        color: '#d32f2f'
      }
    }
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '15px'
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
}));

export default React.memo(ShareDeviceModal);
